<template>
  <div>
    <card id="endDeviceProperties" no-header no-body no-footer>
      <table slot="card-table" class="table table-sm table-ellipsis mb-0">
        <tbody>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:devEui') }}</td>
            <td>{{ endDevice.devEui }}</td>
          </tr>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:name') }}</td>
            <td>{{ endDevice.name }}</td>
          </tr>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:cluster') }}</td>
            <td>{{ endDevice.cluster ? endDevice.cluster.name : '' }}</td>
          </tr>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:classType') }}</td>
            <td>{{ endDevice.classType }}</td>
          </tr>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:region') }}</td>
            <td>{{ endDevice.rfRegion }}<span v-if="endDevice.country"> ({{ endDevice.country }})</span></td>
          </tr>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:version') }}</td>
            <td>{{ endDevice.macVersion }} {{ endDevice.regParamsRevision }}</td>
          </tr>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:adr') }}</td>
            <td>{{ $t('ed-properties:adrState.'+endDevice.adrEnabled) | capitalize }}</td>
          </tr>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:status') }}</td>
            <td>{{ status || $t('ed-properties:unknown') | capitalize }}</td>
          </tr>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:profile') }}</td>
            <td>{{ profile | capitalize }}</td>
          </tr>
          <tr>
            <td class="text-uppercase">{{ $t('ed-properties:counters') }}</td>
            <td>
              <div v-if="isRole.user && endDevice.activation === 'ABP'" class="float-right">
                <div id="endDeviceProperties-setFcnt"
                     :title="$t('ed-properties:setFcnt') | capitalize"
                     class="btn btn-link pointer p-0 d-block text-right"
                     @click.prevent="displayFcntForm"
                >
                  {{ $t('ed-properties:set') | capitalize }}
                </div>

                <div id="endDeviceProperties-resetFcnt"
                     :title="$t('ed-properties:resetFcnt') | capitalize"
                     class="btn btn-link pointer p-0 d-block text-right"
                     @click.prevent="resetFcnt"
                >
                  {{ $t('ed-properties:reset') | capitalize }}
                </div>
              </div>

              <div>
                <div>{{ $t('ed-properties:up') }}: {{ fcntUp }}</div>
                <div>{{ $t('ed-properties:down') }}: {{ fcntDown }}</div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </card>

    <modal v-if="isRole.user && endDevice.activation === 'ABP'"
           id="fcntForm"
           v-model="fcntFormVisible"
           :title="$t('ed-properties:setFcnt')"
           :callback="setFcnt"
           size="sm"
    >
      <div slot="modal-body">
        <div class="form-group">
          <label for="newFcntUp" class="control-label">{{ $t('ed-properties:newFcntUp') | capitalize }}</label>
          <input id="newFcntUp"
                 key="newFcntUp"
                 v-model="newFcntUp"
                 v-validate="'integer|number'"
                 :data-vv-as="$t('ed-properties:newFcntUp')"
                 :class="{ 'is-invalid': errors.has('newFcntUp') }"
                 type="text"
                 name="newFcntUp"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('newFcntUp') }}</div>
          <small class="form-text text-muted">{{ $t('ed-properties:currentValue', { value: fcntUp }) | capitalize }}</small>
        </div>

        <div class="form-group">
          <label for="newFcntDown" class="control-label">{{ $t('ed-properties:newFcntDown') | capitalize }}</label>
          <input id="newFcntDown"
                 key="newFcntDown"
                 v-model="newFcntDown"
                 v-validate="'integer|number'"
                 :data-vv-as="$t('ed-properties:newFcntDown')"
                 :class="{ 'is-invalid': errors.has('newFcntDown') }"
                 type="text"
                 name="newFcntDown"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('newFcntDown') }}</div>
          <small class="form-text text-muted">{{ $t('ed-properties:currentValue', { value: fcntDown }) | capitalize }}</small>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { isNil, isEmpty } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

export default {
  props: {
    endDevice: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      fcntFormVisible: false,
      newFcntUp: '',
      newFcntDown: ''
    }
  },

  computed: {
    status () {
      return this.endDevice.status ? this.$t('endDevices:statuses.' + this.endDevice.status) : ''
    },

    profile () {
      return this.endDevice.profile ? this.$t('endDevices:profiles.' + this.endDevice.profile) : ''
    },

    fcntUp () { return isNil(this.endDevice.fcntUp) ? '-' : this.endDevice.fcntUp },
    fcntDown () { return isNil(this.endDevice.fcntDown) ? '-' : this.endDevice.fcntDown }
  },

  methods: {
    displayFcntForm () {
      this.fcntFormVisible = true
      this.newFcntUp = ''
      this.newFcntDown = ''
    },

    async resetFcnt () {
      let error
      [error] = await to(this.$api.resetEndDeviceFrameCounters(this.endDevice))
      if (error) return

      // Reset in local store
      this.$store.commit('resetCounters')
      // Success
      eventBus.$emit('message-success', this.$t('ed-properties:resetSuccess'))
    },

    async setFcnt () {
      const endDevice = {}
      if (this.newFcntUp) endDevice.fcntUp = Number(this.newFcntUp)
      if (this.newFcntDown) endDevice.fcntDown = Number(this.newFcntDown)

      if (isEmpty(endDevice)) return

      let error
      [error] = await to(this.$api.patchEndDevice(this.endDevice, endDevice))
      if (error) return

      this.fcntFormVisible = false
      // Reset in local store
      this.$store.commit('setCounters', endDevice)
      // Success
      eventBus.$emit('message-success', this.$t('ed-properties:setSuccess'))
    }
  }
}
</script>
